import BaseTypeController from "vamos/cockpit/base/controller/BaseTypeController";
import ListHandler from "vamos/cockpit/base/handler/ListHandler";
import Table, { Table$FilterEvent, Table$SortEvent } from "sap/ui/table/Table";
import JSONModel from "sap/ui/model/json/JSONModel";
import JSONFilterFieldDelegate from "vamos/cockpit/base/delegates/JSONFilterFieldDelegate";
import Filter from "sap/ui/model/Filter";
import Sorter from "sap/ui/model/Sorter";
import FilterOperator from "sap/ui/model/FilterOperator";
import getText from "vamos/cockpit/base/handler/Text";
import { DiscontinuedArticle } from "vamos/cockpit/model/odata/VamosModel";
import { SearchField$SearchEvent } from "sap/m/SearchField";
import VariantManagementService from "vamos/cockpit/base/services/VariantManagementService";
import P13nService from "vamos/cockpit/base/services/P13nService";
import formatter from "vamos/cockpit/base/formatter/formatter";
import ReplaceDiscontinuedArticleDialogController from "vamos/cockpit/data/masterdata/bmecat/discontinueditems/controller/ReplaceDiscontinuedArticleDialog.controller";
import Button, { Button$PressEvent } from "sap/m/Button";
import ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import ViewHandler from "vamos/cockpit/base/handler/ViewHandler";
import IgnoreReplacementDialogController from "vamos/cockpit/data/masterdata/bmecat/discontinueditems/controller/IgnoreReplacementDialog.controller";
import V4Context from "sap/ui/model/odata/v4/Context";
import MultiSelectionPlugin, { MultiSelectionPlugin$SelectionChangeEvent } from "sap/ui/table/plugins/MultiSelectionPlugin";
import PositionDialogInterface from "vamos/cockpit/data/masterdata/bmecat/discontinueditems/model/PositionDialogInterface";
import { Link$PressEvent } from "sap/m/Link";
import XMLView from "sap/ui/core/mvc/XMLView";
import RequestPositionDialogController from "vamos/cockpit/data/masterdata/bmecat/discontinueditems/controller/RequestPositionDialog.controller";
import OrderPositionDialogController from "vamos/cockpit/data/masterdata/bmecat/discontinueditems/controller/OrderPositionDialog.controller";
import { DiscontinuedArticleStatusType } from "vamos/cockpit/data/masterdata/bmecat/discontinueditems/model/DiscontinuedArticleStatusType";
import { showWarning } from "vamos/cockpit/base/handler/MessageToastHandler";

/**
 * @namespace vamos.cockpit.data.masterdata.bmecat.discontinueditems.controller
 */

export default class DiscontinuedArticlesController extends BaseTypeController {
    private listHandler: ListHandler;
    public variantManagement: VariantManagementService = VariantManagementService;
    public p13nService: P13nService = P13nService;
    public formatter: formatter = formatter;
    private discontinuedArticlesTable:Table;
    private selectionPlugin:MultiSelectionPlugin;

    private _oDefaultVariantContext: any = {
        discontinuedArticlesTable: {
            conditions: {
                "*ArticleNumber,ArticleName*": { $search: null },
                Status: [
                    { operator: "EQ", values: [DiscontinuedArticleStatusType.NEW, { path: "i18n>New" }] },
                    { operator: "EQ", values: [DiscontinuedArticleStatusType.NOT_REPLACED, { path: "i18n>NotReplaced" }] },
                ],
            },
            sorters: { Version: "Descending" },
            filters: {},
            width: {},
            states: {},
        },
    };
    private searchQuery: string;

    public onInit() {
        super.onInit();
        this.selectionPlugin = this.byId("discontinuedArticlesTableSelectionPlugin") as MultiSelectionPlugin;
        this.discontinuedArticlesTable = this.byId("discontinuedArticlesTable") as Table;

        const vh = this.getModel("ValueHelp") as JSONModel;
        const statuses = [
            { Key: DiscontinuedArticleStatusType.NEW, DisplayName: getText("New") },
            { Key: DiscontinuedArticleStatusType.NOT_REPLACED, DisplayName: getText("NotReplaced") },
            { Key: DiscontinuedArticleStatusType.REPLACED, DisplayName: getText("Replaced") },
            { Key: DiscontinuedArticleStatusType.IGNORED, DisplayName: getText("Ignored") },
        ];

        vh.setProperty("/DiscontinuedArticleStatus", statuses);
    }

    public afterVariantsLoaded(model: JSONModel) {
        this.tableModel = model;
        this.getView().setModel(model, "tables");

        this.listHandler = new ListHandler(this.discontinuedArticlesTable, this.viewModel, model);

        const filters: Filter | Filter[] = JSONFilterFieldDelegate.getFilterFromTableModel.call(this, model, this.discontinuedArticlesTable, "/DiscontinuedArticles");
        const filterParam = Array.isArray(filters) ? filters : [filters];
        const sorter: Sorter | undefined = JSONFilterFieldDelegate.getSorterFromTableModel.call(this, model, this.discontinuedArticlesTable);
        this.listHandler.bindTable("DiscontinuedArticle", { $expand: "ProductDetail" }, filterParam, sorter);
    }

    public onSort(event: Table$SortEvent) {
        event.preventDefault();
        this.listHandler.onSort(event, this);
    }

    public async onFilter(event: Table$FilterEvent) {
        event.preventDefault();
        event.getParameter("column")?.setFilterValue(event.getParameter("value"));

        await this.filterTable();
    }

    public async onSearch(event: SearchField$SearchEvent) {
        this.searchQuery = event.getParameter("query");
        await this.filterTable();
    }

    private async filterTable() {
        const table: Table = this.byId("discontinuedArticlesTable") as Table;
        const binding = table.getBinding("rows") as ODataListBinding;

        const filters: Filter[] = [];

        const normalFilterKeys = ["ArticleNumber", "ArticleName"];

        if (this.searchQuery) {
            const normalFilters: Filter[] = normalFilterKeys.map((filter: string): Filter => new Filter({
                    path: filter,
                    operator: FilterOperator.Contains,
                    value1: this.searchQuery,
                    caseSensitive: false,
                }),
            );

            filters.push(new Filter(normalFilters, false));
        }
        const model = await this.variantsLoaded as JSONModel;
        const jsonFilters: Filter | Filter[] = JSONFilterFieldDelegate.getFilterFromTableModel.call(this, model, table, "/DiscontinuedArticles");
        if (!Array.isArray(jsonFilters)) filters.push(jsonFilters);

        binding.filter(filters);
    }

    public async onOpenReplaceDiscontinuedArticles(oEvent?: Button$PressEvent): Promise<void> {
        const table = this.byId("discontinuedArticlesTable") as Table;
        let contexts: any[];

        const oRowContext = oEvent?.getSource?.().getBindingContext?.();
        if (oRowContext) {
            contexts = [oRowContext];
        } else {
            const indices = this.filterSelectedItemsNotIgnored();
            contexts = indices.map((index) => this.discontinuedArticlesTable.getContextByIndex(index));
        }

        if (!contexts.length) return;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-member-access
        const discontinuedArticles = contexts.map((c) => c?.getObject());
        const view = await ViewHandler.getView("vamos.cockpit.data.masterdata.bmecat.discontinueditems.view.ReplaceDiscontinuedArticleDialog", this.getComponent());
        const controller = view.getController() as ReplaceDiscontinuedArticleDialogController;
        controller.openDialog(discontinuedArticles, () => {
            this.onSaveDone();
        });
    }

    public onSaveDone() {
        const oTable = this.byId("discontinuedArticlesTable") as Table | undefined;
        oTable?.getBinding("rows")?.refresh();
        this.selectionPlugin.clearSelection();
    }
    public async onIgnoreSelected():Promise<void>{
        const indices:number[] =this.filterSelectedItemsNotIgnored();
        const guids:string[] = indices.map((index):string => {
            const context = this.discontinuedArticlesTable.getContextByIndex(index);
            const discontinuedArticle:DiscontinuedArticle = context.getObject() as DiscontinuedArticle;
            return discontinuedArticle.Guid;
        });
        await this.openIgnoreDialog(guids);
    }
    public async onIgnore(event: Button$PressEvent):Promise<void>{
        const button:Button = event.getSource();
        const context:V4Context = button.getBindingContext() as V4Context;
        const guid = context.getProperty("Guid") as string;
        await this.openIgnoreDialog([guid]);
    }
    public onSelectionChange(event: MultiSelectionPlugin$SelectionChangeEvent){
        this.deselectReplacedArticles(event);

        this.listHandler.onSelectionChangePlugin(event);

        const indices:number[] = this.filterSelectedItemsNotIgnored();

        this.viewModel.setProperty("/hasNotIgnoredSelected", indices.length > 0);
    }
    private deselectReplacedArticles(event: MultiSelectionPlugin$SelectionChangeEvent){
        const plugin = event.getSource();
        const changedIndices:number[] = event.getParameter("indices") ?? [];
        let hasDeselectedReplacedArticle = false;

        changedIndices.forEach((index) => {
            if (!plugin.isIndexSelected(index)) return;
            const context = this.discontinuedArticlesTable.getContextByIndex(index);
            const discontinuedArticle = context?.getObject() as DiscontinuedArticle | undefined;
            if (discontinuedArticle?.Status === DiscontinuedArticleStatusType.REPLACED) {
                plugin.removeSelectionInterval(index, index);
                hasDeselectedReplacedArticle = true;
            }
        });

        if (hasDeselectedReplacedArticle) {
            showWarning(getText("ReplacedArticleNotSelectable"));
        }
    }
    public async openIgnoreDialog(guids:string[]):Promise<void>{
        const view = await ViewHandler.getView("vamos.cockpit.data.masterdata.bmecat.discontinueditems.view.IgnoreReplacementDialog", this.getComponent());
        const controller = view.getController() as IgnoreReplacementDialogController;
        controller.openDialog(guids, () => {
            this.onSaveDone();
        });
    }
    public filterSelectedItemsNotIgnored():number[]{
        return this.selectionPlugin.getSelectedIndices().filter((index) => {
            const context = this.discontinuedArticlesTable.getContextByIndex(index);
            if(!context) return false;
            const discontinuedArticle:DiscontinuedArticle = context.getObject() as DiscontinuedArticle;
            return discontinuedArticle.Status !== DiscontinuedArticleStatusType.IGNORED
                && discontinuedArticle.Status !== DiscontinuedArticleStatusType.REPLACED;
        });
    }
    public async onOpenRequestPositions(event: Link$PressEvent){
        const view:XMLView = await ViewHandler.getView("vamos.cockpit.data.masterdata.bmecat.discontinueditems.view.RequestPositionDialog", this.getComponent());
        const controller = view.getController() as RequestPositionDialogController;
        const discontinuedArticle = event.getSource().getBindingContext()?.getObject() as DiscontinuedArticle;
        const payload:PositionDialogInterface = {
            articleName: discontinuedArticle.ArticleName,
            productName: discontinuedArticle.Variant,
            productGuid: discontinuedArticle.ProductGuid,
            count: discontinuedArticle.UnextractedRequestPositionCount
        }
        controller.openDialog(payload);
    }
    public async onOpenOrderPositions(event:Link$PressEvent){
        const view:XMLView = await ViewHandler.getView("vamos.cockpit.data.masterdata.bmecat.discontinueditems.view.OrderPositionDialog", this.getComponent());
        const controller = view.getController() as OrderPositionDialogController;
        const discontinuedArticle = event.getSource().getBindingContext()?.getObject() as DiscontinuedArticle;
        const payload:PositionDialogInterface = {
            articleName: discontinuedArticle.ArticleName,
            productName: discontinuedArticle.Variant,
            productGuid: discontinuedArticle.ProductGuid,
            count: discontinuedArticle.OpenOrderPositionCount
        }
        controller.openDialog(payload);

    }

}